/**
 * IconButton — bouton icône avec tooltip CSS pur (Tailwind group)
 * Usage : <IconButton icon={Eye} label="Voir" href="/..." />
 *         <IconButton icon={Trash2} label="Supprimer" variant="danger" onClick={...} />
 */

import React from 'react';
import { Link } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';

export type IconButtonVariant = 'default' | 'danger' | 'success' | 'warning';

interface IconButtonProps {
  icon: LucideIcon;
  label: string;
  onClick?: () => void;
  href?: string;
  disabled?: boolean;
  variant?: IconButtonVariant;
  className?: string;
}

const variantClasses: Record<IconButtonVariant, string> = {
  default: 'text-content-muted hover:text-accent hover:bg-accent-light',
  danger:  'text-red-500 hover:text-red-700 hover:bg-red-50',
  success: 'text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50',
  warning: 'text-amber-600 hover:text-amber-700 hover:bg-amber-50',
};

export function IconButton({
  icon: Icon,
  label,
  onClick,
  href,
  disabled = false,
  variant = 'default',
  className = '',
}: IconButtonProps) {
  const baseClass = `rounded-lg p-1.5 transition-colors ${variantClasses[variant]} ${
    disabled ? 'opacity-40 pointer-events-none' : ''
  } ${className}`;

  const tooltip = (
    <span
      className={`
        pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5
        whitespace-nowrap rounded px-2 py-1 text-xs font-medium
        bg-gray-800 text-white opacity-0 group-hover:opacity-100
        transition-opacity duration-150 z-50
      `}
    >
      {label}
    </span>
  );

  return (
    <div className="relative group inline-flex">
      {href ? (
        <Link to={href} className={baseClass} aria-label={label}>
          <Icon className="w-4 h-4" />
        </Link>
      ) : (
        <button
          type="button"
          onClick={onClick}
          disabled={disabled}
          className={baseClass}
          aria-label={label}
        >
          <Icon className="w-4 h-4" />
        </button>
      )}
      {tooltip}
    </div>
  );
}
